import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api, { friendlyMessage } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { formatINR, formatDate } from "../../lib/utils.fmt";
import { Search, Plus, Minus, Edit, Trash2, Wallet, Calendar, Phone, X, Package, Package2, ChevronUp, ChevronDown, Upload, Image as ImageIcon, AlertTriangle, History } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { toast } from "sonner";
import EmptyState, { LoadingState } from "../../components/EmptyState";
import AdminCustomerAddress from "../../components/AdminCustomerAddress";
import CustomerUnit from "../../components/CustomerUnit";
import { NO_APARTMENTS, addressChoices, addressError, addressOptions, createPayload, editAddressPayload, isApartmentMode, startAddress, typedFlatError } from "../../lib/adminCustomerAddress";
import Pagination from "../../components/Pagination";
import ProductImage from "../../components/ProductImage";
import ImageCropDialog from "../../components/ImageCropDialog";
import SubscriptionProductChange from "../../components/SubscriptionProductChange";
import { dialogAfterChange, hasUnsavedEdits, isProductChangeOn } from "../../lib/subscriptionProduct";
import { tomorrowIST } from "../../lib/istDate";
import { pauseSummary } from "../../lib/subscriptionPause";
// Deleted routes: labelled in filters, never offered for a new assignment.
import { assignableRoutes, filterableRoutes, routeOptionLabel } from "../../lib/routeOptions";

const PAGE_SIZE = 20;

// ============ CUSTOMERS ============
export function AdminCustomers() {
  const { tenant } = useAuth();
  // When the tenant disables bottle tracking, the Customers table hides the
  // 🍶 Bottles column entirely so admins aren't tempted to drill into it.
  const bottlesOn = tenant?.bottle_tracking_enabled !== false;
  const [customers, setCustomers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [cities, setCities] = useState([]);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [subscribedCount, setSubscribedCount] = useState(0);
  const [active, setActive] = useState(null);
  const [adjAmount, setAdjAmount] = useState(0);

  // create
  const [createOpen, setCreateOpen] = useState(false);
  const createBlank = {
    name: "", phone: "", language: "en", route_id: "",
    address_line1: "", address_line2: "", address_city: "", address_pincode: "",
    address_typed_flat: "",
  };
  const [createForm, setCreateForm] = useState(createBlank);

  // edit
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", phone: "", route_id: "" });

  // Serviceable apartments — the choice a customer has in their own address
  // form. Stays at "no apartments" (the typed form, as it always was) for a
  // store that does not use them, and if the lookup fails.
  const [addrOptions, setAddrOptions] = useState(NO_APARTMENTS);
  const [createAddr, setCreateAddr] = useState(() => startAddress(NO_APARTMENTS, null));
  const [editAddr, setEditAddr] = useState(() => startAddress(NO_APARTMENTS, null));
  // Which saved address the Edit form is editing (a customer can have
  // several; a subscription can deliver to any of them), and — only for a
  // customer with several — their subscriptions, to show which one is used.
  const [editAddrId, setEditAddrId] = useState(null);
  const [editSubs, setEditSubs] = useState(null);
  const editTarget = useRef({ customerId: null, address: null });
  const loadAddressOptions = () => api.get("/api/admin/customers/address-options")
    .then(({ data }) => { const o = addressOptions(data); setAddrOptions(o); return o; })
    .catch(() => { setAddrOptions(NO_APARTMENTS); return NO_APARTMENTS; });

  // place order on behalf
  const [orderFor, setOrderFor] = useState(null);
  const [orderMode, setOrderMode] = useState("once"); // once | subscribe
  const [cart, setCart] = useState([]); // [{product_id, name, price, qty}]
  const [orderSlot, setOrderSlot] = useState("morning");
  const [orderDate, setOrderDate] = useState(() => tomorrowIST());
  const [orderInstructions, setOrderInstructions] = useState("");
  // subscribe-specific
  const [subProductId, setSubProductId] = useState("");
  const [subQty, setSubQty] = useState(1);
  const [subFreq, setSubFreq] = useState("daily");
  const [subDays, setSubDays] = useState([]);
  const [subStart, setSubStart] = useState(() => tomorrowIST());

  // Filters
  const [routeFilter, setRouteFilter] = useState("__all__"); // __all__ | __unassigned__ | <route_id>
  const [cityFilter, setCityFilter] = useState("__all__");
  const [subFilter, setSubFilter] = useState("__all__");     // __all__ | yes | no
  // Sort
  const [sortBy, setSortBy] = useState({ key: "name", dir: "asc" });
  const toggleSort = (key) => setSortBy((s) => s.key === key ? { ...s, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const SortHeader = ({ k, children, align = "left" }) => (
    <th className={`px-5 py-3 select-none cursor-pointer text-${align}`} onClick={() => toggleSort(k)} data-testid={`cust-sort-${k}`}>
      <span className="inline-flex items-center gap-1">
        {children}
        {sortBy.key === k ? (sortBy.dir === "asc" ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>) : null}
      </span>
    </th>
  );

  // Debounce the search box so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Only the newest list request may fill the table: an older one answering
  // late (a search the admin has since refined, a filter since changed) is
  // dropped instead of overwriting the newer result.
  const loadSeq = useRef(0);
  const load = (targetPage) => {
    const seq = ++loadSeq.current;
    setLoadError(null);
    const p = new URLSearchParams();
    p.set("page", String(targetPage ?? page));
    p.set("page_size", String(PAGE_SIZE));
    if (debouncedQ) p.set("q", debouncedQ);
    if (routeFilter !== "__all__") p.set("route_id", routeFilter);
    if (cityFilter !== "__all__") p.set("city", cityFilter);
    if (subFilter !== "__all__") p.set("subscribed", subFilter);
    p.set("sort_by", sortBy.key);
    p.set("sort_dir", sortBy.dir);
    return api.get(`/api/admin/customers?${p}`)
      .then(({ data }) => {
        if (seq !== loadSeq.current) return;
        setCustomers(data.customers);
        setTotal(data.total);
        setTotalPages(data.total_pages);
        setCities(data.cities || []);
        setUnassignedCount(data.unassigned_count || 0);
        setSubscribedCount(data.subscribed_count || 0);
      })
      .catch((e) => { if (seq !== loadSeq.current) return; setLoadError(friendlyMessage(e)); toast.error(friendlyMessage(e)); });
  };

  useEffect(() => {
    load(1).finally(() => setLoading(false));
    api.get("/api/admin/routes").then(({ data }) => setRoutes(data.routes)).catch((e) => toast.error(friendlyMessage(e)));
    api.get("/api/admin/products").then(({ data }) => setProducts((data.products || []).filter((p) => p.is_active))).catch((e) => toast.error(friendlyMessage(e)));
    loadAddressOptions().then((o) => setCreateAddr((a) => (a.touched ? a : startAddress(o, null))));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Any filter/search/sort change re-queries from page 1 — skip the very
  // first render since the mount effect above already loaded page 1.
  const filtersReady = useRef(false);
  useEffect(() => {
    if (!filtersReady.current) { filtersReady.current = true; return; }
    setPage(1);
    setPageLoading(true);
    load(1).finally(() => setPageLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ, routeFilter, cityFilter, subFilter, sortBy]);

  const goToPage = (p) => {
    setPage(p);
    setPageLoading(true);
    load(p).finally(() => setPageLoading(false));
  };

  const adjust = async () => {
    await api.post(`/api/admin/customers/${active.id}/wallet/adjust`, { amount: parseFloat(adjAmount), note: "Admin adjustment" });
    toast.success(`Wallet adjusted by ₹${adjAmount}`);
    setActive(null); setAdjAmount(0); load();
  };

  const assignRoute = async (cid, route_id) => {
    try {
      await api.put(`/api/admin/customers/${cid}/route`, { customer_id: cid, route_id: route_id || null });
      toast.success(route_id ? "Route assigned" : "Route cleared");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Assign failed"); }
  };

  const openCreate = () => {
    setCreateOpen(true);
    // The list may have changed since the page loaded (an apartment just
    // added); what the admin has already chosen is kept.
    loadAddressOptions().then((o) => setCreateAddr((a) => (a.touched ? a : startAddress(o, null))));
  };

  const createCustomer = async () => {
    if (!createForm.name.trim() || !createForm.phone.trim()) {
      toast.error("Name and phone required"); return;
    }
    const addressProblem = addressError(addrOptions, createAddr)
      || typedFlatError(addrOptions, createAddr, createForm, !!(createForm.address_line1 || "").trim());
    if (addressProblem) { toast.error(addressProblem); return; }
    try {
      await api.post("/api/admin/customers", createPayload(createForm, addrOptions, createAddr));
      toast.success("Customer created");
      setCreateOpen(false); setCreateForm(createBlank); setCreateAddr(startAddress(addrOptions, null)); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Create failed"); }
  };

  const addressFields = (addr) => ({
    address_label: addr.label || "Home",
    address_line1: addr.line1 || "",
    address_line2: addr.line2 || "",
    address_city: addr.city || "",
    address_pincode: addr.pincode || "",
    address_landmark: addr.landmark || "",
    // a typed address's own flat (an apartment's flat lives in the picker)
    address_typed_flat: addr.apartment_id ? "" : (addr.flat_number || ""),
  });
  const openEdit = (c) => {
    setEditing(c);
    const addr = (c.addresses && c.addresses[0]) || {};
    editTarget.current = { customerId: c.id, address: addr };
    setEditAddrId(addr.id || null);
    setEditForm({ name: c.name || "", phone: c.phone || "", route_id: c.route_id || "", ...addressFields(addr) });
    // An apartment address reopens on its apartment and flat number.
    setEditAddr(startAddress(addrOptions, addr));
    loadAddressOptions().then((o) => setEditAddr((a) => (a.touched ? a : startAddress(o, editTarget.current.address))));
    setEditSubs(null);
    if ((c.addresses || []).length > 1) {
      api.get(`/api/admin/customers/${c.id}/subscriptions`)
        .then(({ data }) => { if (editTarget.current.customerId === c.id) setEditSubs(data.subscriptions || []); })
        .catch(() => {});
    }
  };
  // Switch the form to another saved address: its fields replace the address
  // part of the form (name, phone and route stay as typed).
  const pickEditAddress = (id) => {
    const addr = (editing?.addresses || []).find((a) => a.id === id);
    if (!addr) return;
    editTarget.current = { ...editTarget.current, address: addr };
    setEditAddrId(id);
    setEditForm((f) => ({ ...f, ...addressFields(addr) }));
    setEditAddr(startAddress(addrOptions, addr));
  };
  const saveEdit = async () => {
    if (!editing) return;
    const addressProblem = addressError(addrOptions, editAddr);
    if (addressProblem) { toast.error(addressProblem); return; }
    try {
      const payload = {};
      if (editForm.name !== editing.name) payload.name = editForm.name;
      if (editForm.phone !== editing.phone) payload.phone = editForm.phone;
      // Only what changed; an apartment address sends its apartment and flat
      // number, never the street fields (lib/adminCustomerAddress.js).
      const original = (editing.addresses || []).find((a) => a.id === editAddrId);
      const addressPart = editAddressPayload(original, editForm, addrOptions, editAddr);
      // In a store that requires a flat number, an address being changed must
      // end up with one (the next edit is where a missing one gets added).
      const flatProblem = typedFlatError(addrOptions, editAddr, editForm, Object.keys(addressPart).length > 0);
      if (flatProblem) { toast.error(flatProblem); return; }
      // Name the address being edited, so the change can only land on it.
      if (original && Object.keys(addressPart).length > 0) addressPart.address_id = original.id;
      Object.assign(payload, addressPart);
      if ((editForm.route_id || "") !== (editing.route_id || "")) {
        // route changes via dedicated route-assign endpoint
        await api.put(`/api/admin/customers/${editing.id}/route`, {
          customer_id: editing.id, route_id: editForm.route_id || null,
        });
      }
      if (Object.keys(payload).length > 0) {
        await api.patch(`/api/admin/customers/${editing.id}`, payload);
      }
      toast.success("Customer updated");
      setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  const openOrderFor = (c) => {
    setOrderFor(c);
    setOrderMode("once");
    setCart([]);
    setOrderSlot("morning");
    setOrderInstructions("");
    setSubProductId(products[0]?.id || "");
    setSubQty(1); setSubFreq("daily"); setSubDays([]);
  };

  const addToCart = (p) => {
    setCart((cur) => {
      const ex = cur.find((x) => x.product_id === p.id);
      if (ex) return cur.map((x) => x.product_id === p.id ? { ...x, qty: x.qty + 1 } : x);
      return [...cur, { product_id: p.id, name: p.name, price: p.price, qty: 1 }];
    });
  };
  const updateCartQty = (pid, qty) => {
    setCart((cur) => qty <= 0 ? cur.filter((x) => x.product_id !== pid) : cur.map((x) => x.product_id === pid ? { ...x, qty } : x));
  };

  const placeOrderOnBehalf = async () => {
    if (!orderFor) return;
    try {
      if (orderMode === "once") {
        if (cart.length === 0) { toast.error("Add items to cart"); return; }
        await api.post(`/api/admin/customers/${orderFor.id}/orders/cart`, {
          items: cart.map((x) => ({ product_id: x.product_id, quantity: x.qty })),
          slot: orderSlot, delivery_date: orderDate, instructions: orderInstructions,
        });
        toast.success("Order placed for customer");
      } else {
        if (!subProductId) { toast.error("Pick a product"); return; }
        if ((subFreq === "weekly" || subFreq === "custom") && subDays.length === 0) {
          toast.error("Pick at least one day"); return;
        }
        await api.post(`/api/admin/customers/${orderFor.id}/subscriptions`, {
          product_id: subProductId, quantity: subQty,
          frequency: subFreq, custom_days: (subFreq === "weekly" || subFreq === "custom") ? subDays : [],
          slot: orderSlot, start_date: subStart, instructions: orderInstructions,
        });
        toast.success("Subscription created for customer");
      }
      setOrderFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const cartTotal = cart.reduce((a, b) => a + b.qty * b.price, 0);

  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load customers" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="admin-customers-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Customer Base</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Customers ({total})</h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input placeholder="Search by name or phone" className="pl-10 h-11 rounded-2xl w-full sm:w-72" value={q} onChange={(e) => setQ(e.target.value)} data-testid="search-input"/>
          </div>
          <Button onClick={openCreate} className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="add-customer-btn">
            <Plus className="w-4 h-4 mr-1"/> Add Customer
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mr-1">Filters</span>
        <select value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm border border-neutral-200 bg-white font-bold"
                data-testid="cust-route-filter">
          <option value="__all__">All routes</option>
          <option value="__unassigned__">{`Unassigned (${unassignedCount})`}</option>
          {filterableRoutes(routes).map((r) => <option key={r.id} value={r.id}>{routeOptionLabel(r)}</option>)}
        </select>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm border border-neutral-200 bg-white font-bold"
                data-testid="cust-city-filter">
          <option value="__all__">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={subFilter} onChange={(e) => setSubFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm border border-neutral-200 bg-white font-bold"
                data-testid="cust-sub-filter">
          <option value="__all__">All customers</option>
          <option value="yes">{`Subscribed (${subscribedCount})`}</option>
          <option value="no">Not subscribed</option>
        </select>
        {(routeFilter !== "__all__" || cityFilter !== "__all__" || subFilter !== "__all__") && (
          <button onClick={() => { setRouteFilter("__all__"); setCityFilter("__all__"); setSubFilter("__all__"); }}
                  className="h-10 px-3 rounded-full text-xs font-bold bg-brand-clay/10 text-brand-clay inline-flex items-center gap-1"
                  data-testid="cust-clear-filters">
            <X className="w-3 h-3"/> Clear
          </button>
        )}
        <span className="text-xs text-neutral-500 ml-auto" data-testid="cust-count">
          Page {page} of {totalPages} · {total} total
        </span>
      </div>

      <div className={`bg-white rounded-3xl border border-neutral-200 overflow-hidden ${pageLoading ? "opacity-60" : ""}`}>
        {/* Mobile card list */}
        <ul className="md:hidden divide-y divide-neutral-100" data-testid="cust-mobile-list">
          {customers.length === 0 && <li><EmptyState icon={Search} size="sm" title="No customers match" /></li>}
          {customers.map((c) => (
            <li key={c.id} className="p-4" data-testid={`cust-mobile-${c.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-base truncate">{c.name}</div>
                  <div className="text-xs text-neutral-500 font-mono mt-0.5">
                    {(() => {
                      const ph = (c.phone || "").trim();
                      if (ph.startsWith("+91") && ph.length === 13)
                        return <>+91 {ph.slice(3)}</>;
                      return ph;
                    })()}
                  </div>
                  <CustomerUnit address={c.addresses?.[0]} requireFlat={tenant?.require_flat_number === true} className="mt-0.5" testId={`cust-unit-${c.id}`}/>
                  <div className="text-xs text-neutral-500 mt-0.5 truncate" title={c.addresses?.[0]?.line1 || ""}>
                    {c.addresses?.[0]?.city || "—"}{c.addresses?.[0]?.line1 ? " · " + c.addresses[0].line1 : ""}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className={`font-mono font-bold text-base ${c.wallet_balance < 100 ? "text-brand-clay" : ""}`}>{formatINR(c.wallet_balance)}</div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">
                    {c.active_subs} subs{bottlesOn && c.bottles_held ? ` · 🍶 ${c.bottles_held}` : ""}
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <select value={c.route_id || ""} onChange={(e) => assignRoute(c.id, e.target.value)}
                        className="h-9 text-xs rounded-2xl border border-neutral-200 bg-white px-2 w-full"
                        data-testid={`assign-route-${c.id}`}>
                  <option value="">— unassigned route —</option>
                  {assignableRoutes(routes, c.route_id).map((r) => <option key={r.id} value={r.id} disabled={r.disabled}>{routeOptionLabel(r)}</option>)}
                </select>
              </div>
              <div className="flex gap-2 mt-2.5">
                <button onClick={() => openOrderFor(c)} className="flex-1 h-9 rounded-2xl bg-brand-leaf/10 text-brand-leaf text-xs font-bold inline-flex items-center justify-center gap-1" data-testid={`place-order-${c.id}`}>
                  <Package className="w-4 h-4"/> Order
                </button>
                <button onClick={() => openEdit(c)} className="flex-1 h-9 rounded-2xl bg-neutral-100 text-brand-ink text-xs font-bold inline-flex items-center justify-center gap-1" data-testid={`edit-${c.id}`}>
                  <Edit className="w-4 h-4"/> Edit
                </button>
                <button onClick={() => setActive(c)} className="flex-1 h-9 rounded-2xl bg-brand-sky/10 text-brand-sky text-xs font-bold inline-flex items-center justify-center gap-1" data-testid={`adjust-${c.id}`}>
                  <Wallet className="w-4 h-4"/> Wallet
                </button>
                <Link to={`/admin/customers/${c.id}/transactions`} className="flex-1 h-9 rounded-2xl bg-neutral-100 text-brand-ink text-xs font-bold inline-flex items-center justify-center gap-1" data-testid={`history-${c.id}`}>
                  <History className="w-4 h-4"/> History
                </Link>
              </div>
            </li>
          ))}
        </ul>

        <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
            <tr>
              <SortHeader k="name">Name</SortHeader>
              <SortHeader k="phone">Phone</SortHeader>
              <SortHeader k="route">Route</SortHeader>
              <SortHeader k="city">City</SortHeader>
              <SortHeader k="address">Address</SortHeader>
              <SortHeader k="wallet" align="right">Wallet</SortHeader>
              {bottlesOn && <SortHeader k="bottles" align="center">🍶 Bottles</SortHeader>}
              <SortHeader k="active_subs" align="right">Active Subs</SortHeader>
              {/* Pinned to the right edge: on a laptop-width screen the table
                  scrolls sideways inside its box, and Order / Edit / Adjust /
                  History used to sit past that edge, out of sight — the phone
                  list shows them on every card. */}
              <th className="text-right px-5 py-3 sticky right-0 z-10 bg-neutral-50 shadow-[-10px_0_12px_-12px_rgba(0,0,0,0.35)]" data-testid="cust-actions-header">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {customers.map((c) => (
              <tr key={c.id} className="hover:bg-neutral-50" data-testid={`customer-row-${c.id}`}>
                <td className="px-5 py-3">
                  <div className="font-semibold">{c.name}</div>
                  <CustomerUnit address={c.addresses?.[0]} requireFlat={tenant?.require_flat_number === true} className="max-w-[220px]" testId={`cust-unit-row-${c.id}`}/>
                </td>
                <td className="px-5 py-3 font-mono text-sm">
                  {(() => {
                    const ph = (c.phone || "").trim();
                    if (ph.startsWith("+91") && ph.length === 13)
                      return <><span className="text-neutral-400">+91 </span>{ph.slice(3)}</>;
                    return ph;
                  })()}
                </td>
                <td className="px-5 py-3">
                  <select value={c.route_id || ""} onChange={(e) => assignRoute(c.id, e.target.value)}
                          className="h-9 text-sm rounded-2xl border border-neutral-200 bg-white px-2 max-w-[160px]"
                          data-testid={`assign-route-${c.id}`}>
                    <option value="">— unassigned —</option>
                    {assignableRoutes(routes, c.route_id).map((r) => <option key={r.id} value={r.id} disabled={r.disabled}>{routeOptionLabel(r)}</option>)}
                  </select>
                </td>
                <td className="px-5 py-3 text-sm">{c.addresses?.[0]?.city || "—"}</td>
                <td className="px-5 py-3 text-sm max-w-[220px] truncate" title={c.addresses?.[0]?.line1 || ""} data-testid={`customer-address-${c.id}`}>
                  {c.addresses?.[0]?.line1 || "—"}
                </td>
                <td className="px-5 py-3 text-right font-mono font-bold">
                  <span className={c.wallet_balance < 100 ? "text-brand-clay" : ""}>{formatINR(c.wallet_balance)}</span>
                </td>
                {bottlesOn && (
                  <td className="px-5 py-3 text-center">
                    <Link to={`/admin/customers/${c.id}/bottles`} className="font-heading font-extrabold text-brand-sky hover:underline" data-testid={`bottles-link-${c.id}`}>
                      🍶 {c.bottles_held || 0}
                    </Link>
                  </td>
                )}
                <td className="px-5 py-3 text-right">{c.active_subs}</td>
                <td className="px-5 py-3 text-right sticky right-0 z-10 bg-white shadow-[-10px_0_12px_-12px_rgba(0,0,0,0.35)]" data-testid={`customer-actions-${c.id}`}>
                  <div className="inline-flex gap-2 flex-wrap justify-end">
                    <button onClick={() => openOrderFor(c)} className="text-brand-leaf text-sm font-bold inline-flex items-center gap-1" data-testid={`place-order-${c.id}`}>
                      <Package className="w-4 h-4"/> Order
                    </button>
                    <button onClick={() => openEdit(c)} className="text-brand-ink text-sm font-bold inline-flex items-center gap-1" data-testid={`edit-${c.id}`}>
                      <Edit className="w-4 h-4"/> Edit
                    </button>
                    <button onClick={() => setActive(c)} className="text-brand-sky text-sm font-bold inline-flex items-center gap-1" data-testid={`adjust-${c.id}`}>
                      <Wallet className="w-4 h-4" /> Adjust
                    </button>
                    <Link to={`/admin/customers/${c.id}/transactions`} className="text-brand-ink text-sm font-bold inline-flex items-center gap-1 hover:underline" data-testid={`history-${c.id}`}>
                      <History className="w-4 h-4" /> History
                    </Link>
                  </div>
                </td>
              </tr>
            ))}
            {customers.length === 0 && <tr><td colSpan="9"><EmptyState icon={Search} size="sm" title="No customers match" /></td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      <Pagination page={page} totalPages={totalPages} total={total} pageSize={PAGE_SIZE}
                  onPageChange={goToPage} loading={pageLoading} testId="cust-pagination" />

      {/* Wallet adjust dialog */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Adjust Wallet</DialogTitle></DialogHeader>
          {active && (
            <>
              <div className="bg-brand-cream rounded-2xl p-4">
                <div className="font-bold">{active.name}</div>
                <div className="text-sm">{active.phone} · Current: {formatINR(active.wallet_balance)}</div>
              </div>
              <Input type="number" value={adjAmount} onChange={(e) => setAdjAmount(e.target.value)}
                     placeholder="Amount (+credit / -debit)" className="h-14 rounded-2xl text-lg" data-testid="adjust-amount"/>
              <Button onClick={adjust} className="h-14 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="confirm-adjust">
                Apply Adjustment
              </Button>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create customer dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="create-customer-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">New Customer</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Full name *" value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} className="h-12 rounded-2xl" data-testid="new-customer-name"/>
            <Input placeholder="Phone (10-digit or +91…) *" value={createForm.phone} onChange={(e) => setCreateForm({ ...createForm, phone: e.target.value })} className="h-12 rounded-2xl" data-testid="new-customer-phone"/>
            <select value={createForm.route_id} onChange={(e) => setCreateForm({ ...createForm, route_id: e.target.value })}
                    className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white w-full" data-testid="new-customer-route">
              <option value="">— no route —</option>
              {assignableRoutes(routes, createForm.route_id).map((r) => <option key={r.id} value={r.id} disabled={r.disabled}>{routeOptionLabel(r)}</option>)}
            </select>
            <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 pt-2">Address (optional)</div>
            <AdminCustomerAddress options={addrOptions} picker={createAddr} onChange={setCreateAddr} testIdPrefix="new-customer-address">
            {addrOptions.require_flat && !isApartmentMode(addrOptions, createAddr) && (
              <Input placeholder="Flat / House no. *" value={createForm.address_typed_flat} onChange={(e) => setCreateForm({ ...createForm, address_typed_flat: e.target.value })} className="h-12 rounded-2xl" data-testid="new-customer-typed-flat"/>
            )}
            <Input placeholder="Address line 1" value={createForm.address_line1} onChange={(e) => setCreateForm({ ...createForm, address_line1: e.target.value })} className="h-12 rounded-2xl" data-testid="new-customer-addr-line1"/>
            <Input placeholder="Address line 2" value={createForm.address_line2} onChange={(e) => setCreateForm({ ...createForm, address_line2: e.target.value })} className="h-12 rounded-2xl"/>
            <div className="grid grid-cols-2 gap-2">
              <Input placeholder="City" value={createForm.address_city} onChange={(e) => setCreateForm({ ...createForm, address_city: e.target.value })} className="h-12 rounded-2xl" data-testid="new-customer-city"/>
              <Input placeholder="Pincode" value={createForm.address_pincode} onChange={(e) => setCreateForm({ ...createForm, address_pincode: e.target.value })} className="h-12 rounded-2xl" data-testid="new-customer-pincode"/>
            </div>
            </AdminCustomerAddress>
            <Button onClick={createCustomer} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-customer-btn">Save Customer</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit customer dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-md rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="edit-customer-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit Customer</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Name</label>
                <Input value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className="h-12 rounded-2xl mt-1" data-testid="edit-customer-name"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Phone</label>
                <Input value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className="h-12 rounded-2xl mt-1" data-testid="edit-customer-phone"/>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">Route</label>
                <select value={editForm.route_id} onChange={(e) => setEditForm({ ...editForm, route_id: e.target.value })}
                        className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white w-full mt-1" data-testid="edit-customer-route">
                  <option value="">— no route —</option>
                  {assignableRoutes(routes, editForm.route_id).map((r) => <option key={r.id} value={r.id} disabled={r.disabled}>{routeOptionLabel(r)}</option>)}
                </select>
              </div>
              <div className="pt-3 border-t border-neutral-100">
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">Delivery Address</div>
                {(editing.addresses || []).length > 1 && (
                  <div className="mb-2">
                    <select value={editAddrId || ""} onChange={(e) => pickEditAddress(e.target.value)}
                            className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white w-full text-sm font-bold"
                            aria-label="Address to edit" data-testid="edit-customer-address-pick">
                      {addressChoices(editing.addresses, editSubs).map((o) => <option key={o.id} value={o.id}>{o.text}</option>)}
                    </select>
                    <div className="text-[11px] text-neutral-500 mt-1">This customer has {editing.addresses.length} saved addresses. Changes apply to the one chosen here, and to its upcoming deliveries.</div>
                  </div>
                )}
                <AdminCustomerAddress options={addrOptions} picker={editAddr} onChange={setEditAddr} spacing="space-y-2" testIdPrefix="edit-customer-address">
                {addrOptions.require_flat && !isApartmentMode(addrOptions, editAddr) && (
                  <Input placeholder="Flat / House no. *" value={editForm.address_typed_flat || ""}
                         onChange={(e) => setEditForm({ ...editForm, address_typed_flat: e.target.value })}
                         className="h-12 rounded-2xl mb-2" data-testid="edit-customer-typed-flat"/>
                )}
                <Input placeholder="Address line 1 *" value={editForm.address_line1}
                       onChange={(e) => setEditForm({ ...editForm, address_line1: e.target.value })}
                       className="h-12 rounded-2xl" data-testid="edit-customer-addr-line1"/>
                <Input placeholder="Address line 2 / apartment" value={editForm.address_line2}
                       onChange={(e) => setEditForm({ ...editForm, address_line2: e.target.value })}
                       className="h-12 rounded-2xl mt-2" data-testid="edit-customer-addr-line2"/>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input placeholder="City *" value={editForm.address_city}
                         onChange={(e) => setEditForm({ ...editForm, address_city: e.target.value })}
                         className="h-12 rounded-2xl" data-testid="edit-customer-addr-city"/>
                  <Input placeholder="Pincode *" value={editForm.address_pincode}
                         onChange={(e) => setEditForm({ ...editForm, address_pincode: e.target.value })}
                         className="h-12 rounded-2xl" data-testid="edit-customer-addr-pincode"/>
                </div>
                </AdminCustomerAddress>
                <Input placeholder="Landmark (optional)" value={editForm.address_landmark}
                       onChange={(e) => setEditForm({ ...editForm, address_landmark: e.target.value })}
                       className="h-12 rounded-2xl mt-2" data-testid="edit-customer-addr-landmark"/>
                <div className="text-[11px] text-neutral-500 mt-1">* required when adding a new address</div>
              </div>
              <Button onClick={saveEdit} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-edit-customer-btn">Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Place order on behalf dialog */}
      <Dialog open={!!orderFor} onOpenChange={(o) => !o && setOrderFor(null)}>
        <DialogContent className="max-w-2xl rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="place-order-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl">Place order for {orderFor?.name}</DialogTitle>
          </DialogHeader>
          {orderFor && (
            <div className="space-y-4">
              {!orderFor.addresses?.[0] && (
                <div className="bg-brand-clay/10 text-brand-clay rounded-2xl p-3 text-sm font-bold" data-testid="no-address-warning">
                  This customer has no address on file. Add one via Edit before placing an order.
                </div>
              )}

              {/* Mode tabs */}
              <div className="grid grid-cols-2 gap-2 p-1 rounded-2xl bg-neutral-100">
                <button onClick={() => setOrderMode("once")}
                        className={`h-10 rounded-xl text-sm font-bold ${orderMode === "once" ? "bg-white shadow-sm" : "text-neutral-600"}`}
                        data-testid="order-mode-once">One-time</button>
                <button onClick={() => setOrderMode("subscribe")}
                        className={`h-10 rounded-xl text-sm font-bold ${orderMode === "subscribe" ? "bg-white shadow-sm" : "text-neutral-600"}`}
                        data-testid="order-mode-subscribe">Subscription</button>
              </div>

              {orderMode === "once" ? (
                <>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">Pick products</div>
                    <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                      {products.map((p) => (
                        <button key={p.id} onClick={() => addToCart(p)}
                                className="text-left p-3 rounded-2xl border border-neutral-200 bg-white hover:bg-brand-cream"
                                data-testid={`pick-product-${p.id}`}>
                          <div className="text-xs font-bold truncate">{p.name}</div>
                          <div className="text-xs text-neutral-500">{p.pack_size}</div>
                          <div className="text-sm font-mono font-bold">{formatINR(p.price)}</div>
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">Cart ({cart.length})</div>
                    {cart.length === 0 ? (
                      <EmptyState icon={Package} size="sm" title="No items yet" className="py-3" testId="cart-empty" />
                    ) : (
                      <div className="space-y-1.5">
                        {cart.map((it) => (
                          <div key={it.product_id} className="flex items-center gap-2 bg-neutral-50 rounded-2xl p-2" data-testid={`admin-cart-item-${it.product_id}`}>
                            <span className="flex-1 text-sm font-semibold truncate">{it.name}</span>
                            <button onClick={() => updateCartQty(it.product_id, it.qty - 1)} className="w-7 h-7 rounded-full bg-white border border-neutral-200" data-testid={`admin-cart-minus-${it.product_id}`}>−</button>
                            <span className="font-mono font-bold w-6 text-center">{it.qty}</span>
                            <button onClick={() => updateCartQty(it.product_id, it.qty + 1)} className="w-7 h-7 rounded-full bg-brand-sky text-white" data-testid={`admin-cart-plus-${it.product_id}`}>+</button>
                            <span className="font-mono text-sm font-bold w-16 text-right">{formatINR(it.qty * it.price)}</span>
                          </div>
                        ))}
                        <div className="flex justify-between font-bold pt-2 border-t border-neutral-200">
                          <span>Total</span><span data-testid="admin-cart-total">{formatINR(cartTotal)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1">Delivery date</div>
                      <Input type="date" value={orderDate} onChange={(e) => setOrderDate(e.target.value)} className="h-12 rounded-2xl" data-testid="admin-order-date"/>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1">Slot</div>
                      <select value={orderSlot} onChange={(e) => setOrderSlot(e.target.value)}
                              className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white w-full" data-testid="admin-order-slot">
                        <option value="morning">Morning</option>
                        <option value="evening">Evening</option>
                      </select>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1">Product</div>
                    <select value={subProductId} onChange={(e) => setSubProductId(e.target.value)}
                            className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white w-full" data-testid="admin-sub-product">
                      <option value="">— pick a product —</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name} · {formatINR(p.price)}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1">Qty</div>
                      <Input type="number" min={1} value={subQty} onChange={(e) => setSubQty(parseInt(e.target.value) || 1)} className="h-12 rounded-2xl" data-testid="admin-sub-qty"/>
                    </div>
                    <div className="col-span-2">
                      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1">Frequency</div>
                      <select value={subFreq} onChange={(e) => setSubFreq(e.target.value)}
                              className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white w-full" data-testid="admin-sub-freq">
                        <option value="daily">Daily</option>
                        <option value="alternate">Alternate</option>
                        <option value="weekly">Weekly</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                  </div>
                  {(subFreq === "weekly" || subFreq === "custom") && (
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-2">Days</div>
                      <div className="grid grid-cols-7 gap-1" data-testid="admin-sub-days">
                        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d, idx) => {
                          const on = subDays.includes(idx);
                          return (
                            <button key={idx} onClick={() => setSubDays((cur) => cur.includes(idx) ? cur.filter((x) => x !== idx) : [...cur, idx].sort())}
                                    className={`h-10 rounded-xl text-xs font-bold border-2 ${on ? "bg-brand-sky text-white border-transparent" : "border-neutral-200 bg-white text-neutral-700"}`}
                                    data-testid={`admin-sub-day-${idx}`}>{d}</button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1">Start date</div>
                      <Input type="date" value={subStart} onChange={(e) => setSubStart(e.target.value)} className="h-12 rounded-2xl" data-testid="admin-sub-start"/>
                    </div>
                    <div>
                      <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1">Slot</div>
                      <select value={orderSlot} onChange={(e) => setOrderSlot(e.target.value)}
                              className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white w-full" data-testid="admin-sub-slot">
                        <option value="morning">Morning</option>
                        <option value="evening">Evening</option>
                        <option value="both">Both</option>
                      </select>
                    </div>
                  </div>
                </>
              )}

              <div>
                <div className="text-xs uppercase tracking-wider font-bold text-neutral-500 mb-1">Instructions</div>
                <Input value={orderInstructions} onChange={(e) => setOrderInstructions(e.target.value)} className="h-12 rounded-2xl" placeholder="e.g., Leave at door" data-testid="admin-order-instructions"/>
              </div>

              <Button onClick={placeOrderOnBehalf}
                      disabled={!orderFor.addresses?.[0] || (orderMode === "once" && cart.length === 0) || (orderMode === "subscribe" && !subProductId)}
                      className="h-14 rounded-2xl bg-brand-leaf hover:bg-green-700 text-white font-extrabold w-full"
                      data-testid="confirm-place-order-btn">
                {orderMode === "once" ? `Place order — ${formatINR(cartTotal)}` : "Create subscription"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ PRODUCTS ============
export function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const blank = { name: "", category_id: "", brand_id: "", pack_size: "500ml", price: 0, mrp: 0, image_url: "", images: [], variants: [], description: "", has_bottle: false };
  const [form, setForm] = useState(blank);
  const [editing, setEditing] = useState(null); // product being edited (null = add mode)
  const [uploading, setUploading] = useState(false);
  const [uploadingVariantIdx, setUploadingVariantIdx] = useState(-1);
  const fileInputRef = useRef(null);
  // Product photos render in a 1:1 aspect-square card everywhere (web +
  // the Capacitor-wrapped mobile app share the same UI), so every upload
  // is cropped to a square before it ever reaches the server — no more
  // guessing whether a rectangular source photo will look right once
  // ProductCard's `object-cover` clips it.
  const [cropSrc, setCropSrc] = useState(null);

  const uploadOne = async (file, folder = "products") => {
    if (!file) return null;
    if (!file.type?.startsWith("image/")) { toast.error("Pick an image file"); return null; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Image too large (max 5 MB)"); return null; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("folder", folder);
    try {
      const { data } = await api.post("/api/admin/upload/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
      return null;
    }
  };

  const uploadImage = async (file) => {
    setUploading(true);
    const url = await uploadOne(file);
    if (url) {
      setForm((f) => {
        const imgs = [...(f.images || [])];
        if (imgs.length >= 5) { toast.error("Max 5 images per product"); return f; }
        imgs.push(url);
        return { ...f, images: imgs, image_url: imgs[0] };
      });
      toast.success("Image uploaded");
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Step 1: file picked → open the crop dialog instead of uploading raw.
  const pickImage = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) { toast.error("Pick an image file"); return; }
    setCropSrc(URL.createObjectURL(file));
  };
  // Step 2: user confirmed the crop → upload the cropped square instead
  // of the original (possibly huge, possibly non-square) source file.
  const handleCropped = async (blob) => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    await uploadImage(new File([blob], "product.jpg", { type: "image/jpeg" }));
  };
  const cancelCrop = () => {
    URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };
  const removeImage = (idx) => {
    setForm((f) => {
      const imgs = (f.images || []).filter((_, i) => i !== idx);
      return { ...f, images: imgs, image_url: imgs[0] || "" };
    });
  };
  const moveImage = (idx, dir) => {
    setForm((f) => {
      const imgs = [...(f.images || [])];
      const j = idx + dir;
      if (j < 0 || j >= imgs.length) return f;
      [imgs[idx], imgs[j]] = [imgs[j], imgs[idx]];
      return { ...f, images: imgs, image_url: imgs[0] || "" };
    });
  };

  // ----- Variants helpers -----
  const addVariant = () => {
    setForm((f) => ({
      ...f,
      variants: [
        ...(f.variants || []),
        { label: "", price: f.price || 0, mrp: f.mrp || 0, pack_size: "", sku: "", stock: 0, is_in_stock: true, image_url: "", display_order: (f.variants || []).length, is_active: true },
      ],
    }));
  };
  const updateVariant = (idx, patch) => {
    setForm((f) => {
      const vs = [...(f.variants || [])];
      vs[idx] = { ...vs[idx], ...patch };
      // Keep the scalar price/pack_size synced to the FIRST variant so
      // legacy readers (subscriptions, invoices etc.) see the default.
      const scalar = idx === 0
        ? { price: parseFloat(vs[0].price) || 0, mrp: parseFloat(vs[0].mrp) || 0, pack_size: vs[0].pack_size || f.pack_size }
        : {};
      return { ...f, ...scalar, variants: vs };
    });
  };
  const removeVariant = (idx) => {
    setForm((f) => {
      const vs = (f.variants || []).filter((_, i) => i !== idx);
      const scalar = vs[0]
        ? { price: parseFloat(vs[0].price) || 0, mrp: parseFloat(vs[0].mrp) || 0, pack_size: vs[0].pack_size || f.pack_size }
        : {};
      return { ...f, ...scalar, variants: vs };
    });
  };
  const uploadVariantImage = async (idx, file) => {
    setUploadingVariantIdx(idx);
    const url = await uploadOne(file, "products/variants");
    if (url) updateVariant(idx, { image_url: url });
    setUploadingVariantIdx(-1);
  };

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const [{ data: p }, { data: c }, { data: b }] = await Promise.all([
        api.get("/api/admin/products"),
        api.get("/api/admin/categories"),
        api.get("/api/admin/brands"),
      ]);
      setProducts(p.products);
      setCategories(c.categories.filter((x) => x.is_active));
      setBrands(b.brands.filter((x) => x.is_active));
    } catch (e) {
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };
  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(blank);
    setOpen(true);
  };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      category_id: p.category_id || "",
      brand_id: p.brand_id || "",
      pack_size: p.pack_size || "",
      price: p.price || 0,
      mrp: p.mrp || 0,
      image_url: p.image_url || "",
      images: p.images && p.images.length ? [...p.images] : (p.image_url ? [p.image_url] : []),
      variants: p.variants && p.variants.length ? p.variants.map((v) => ({ ...v })) : [],
      description: p.description || "",
      has_bottle: !!p.has_bottle,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.price) { toast.error("Name and price required"); return; }
    if (!form.category_id) { toast.error("Pick a category"); return; }
    // Validate variants (if any) — labels + non-negative prices
    for (const [i, v] of (form.variants || []).entries()) {
      if (!v.label || !v.label.trim()) { toast.error(`Variant #${i + 1} needs a label`); return; }
      if (!(v.price >= 0) || !(v.mrp >= 0)) { toast.error(`Variant #${i + 1} needs valid price/MRP`); return; }
    }
    const cat = categories.find((c) => c.id === form.category_id);
    const payload = {
      ...form,
      category: cat?.slug || "",
      brand_id: form.brand_id || null,
      // Ensure image_url mirrors images[0] before send
      image_url: (form.images && form.images[0]) || form.image_url || "",
      images: form.images || [],
      variants: (form.variants || []).map((v) => ({
        ...v,
        price: parseFloat(v.price) || 0,
        mrp: parseFloat(v.mrp) || 0,
        stock: parseInt(v.stock || 0, 10),
        is_in_stock: v.is_in_stock !== false,
      })),
    };
    try {
      if (editing) {
        await api.patch(`/api/admin/products/${editing.id}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/api/admin/products", payload);
        toast.success("Product added");
      }
      setOpen(false); setEditing(null); load();
      setForm(blank);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Disable this product?")) return;
    try {
      await api.delete(`/api/admin/products/${id}`);
      toast.success("Product disabled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const reEnable = async (id) => {
    try {
      await api.patch(`/api/admin/products/${id}`, { is_active: true });
      toast.success("Product re-enabled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || "—";
  const brandName = (id) => brands.find((b) => b.id === id)?.name || "";
  const visible = products.filter((p) => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      (p.name || "").toLowerCase().includes(needle) ||
      (brandName(p.brand_id) || "").toLowerCase().includes(needle) ||
      (catName(p.category_id) || "").toLowerCase().includes(needle) ||
      (p.pack_size || "").toLowerCase().includes(needle)
    );
  });

  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load products" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="admin-products-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Catalog</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Products ({visible.length}/{products.length})</h1>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input placeholder="Search name, brand, category…" className="pl-10 h-11 rounded-2xl w-full sm:w-72" value={q} onChange={(e) => setQ(e.target.value)} data-testid="products-search-input"/>
          </div>
          <Button onClick={openAdd} disabled={categories.length === 0}
                  className="h-11 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full sm:w-auto"
                  data-testid="add-product-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Product
          </Button>
        </div>
      </div>
      {categories.length === 0 && (
        <div className="rounded-2xl bg-brand-sand/15 border border-brand-sand/40 p-4 text-sm text-brand-ink" data-testid="no-categories-warning">
          Create a category first under <b>Categories</b> before adding products.
        </div>
      )}
      {visible.length === 0 && (
        <EmptyState icon={Package} title="No products found" description={q ? "No products match your search." : "Add your first product to start building your catalog."} testId="products-empty" />
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {visible.map((p) => (
          <div key={p.id} className="bg-white rounded-3xl border border-neutral-200 overflow-hidden" data-testid={`product-row-${p.id}`}>
            <ProductImage src={p.image_url} name={p.name} primary="#3A86FF" className="aspect-video" />
            <div className="p-4">
              <div className="text-xs uppercase tracking-wider font-bold text-brand-sky">{catName(p.category_id) || p.category} · {p.pack_size}</div>
              <div className="font-heading font-bold mt-1">{p.name}</div>
              {p.brand_id && brandName(p.brand_id) && (
                <div className="text-[11px] font-bold text-neutral-500 mt-0.5" data-testid={`product-brand-${p.id}`}>
                  by {brandName(p.brand_id)}
                </div>
              )}
              <div className="flex items-center gap-2 mt-2">
                <span className="font-mono font-bold">{formatINR(p.price)}</span>
                <span className="text-xs line-through text-neutral-400">{formatINR(p.mrp)}</span>
                {(() => {
                  const vs = p.variants || [];
                  const allOOS = vs.length > 0 && vs.every((v) => v.is_in_stock === false);
                  if (allOOS) return <span className="text-[10px] font-extrabold px-2 py-0.5 rounded-full bg-brand-clay/10 text-brand-clay ml-auto" data-testid={`product-oos-${p.id}`}>OUT OF STOCK</span>;
                  if (vs.length > 1) return <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand-sky/10 text-brand-sky ml-auto" data-testid={`product-variants-${p.id}`}>{vs.length} variants</span>;
                  return null;
                })()}
                {!p.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-neutral-100 ml-auto">disabled</span>}
              </div>
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <button onClick={() => openEdit(p)} className="text-sm text-brand-sky font-bold inline-flex items-center gap-1" data-testid={`edit-product-${p.id}`}>
                  <Edit className="w-3.5 h-3.5" /> Edit
                </button>
                {p.is_active === false ? (
                  <button onClick={() => reEnable(p.id)} className="text-sm text-brand-leaf font-bold inline-flex items-center gap-1" data-testid={`reenable-product-${p.id}`}>
                    Re-enable
                  </button>
                ) : (
                  <button onClick={() => del(p.id)} className="text-sm text-brand-clay font-bold inline-flex items-center gap-1" data-testid={`delete-${p.id}`}>
                    <Trash2 className="w-3.5 h-3.5" /> Disable
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-lg rounded-3xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 rounded-2xl" data-testid="prod-name" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category_id} onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                      className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white" data-testid="prod-category">
                <option value="">Select category…</option>
                {/* Categories with subcategories render as an <optgroup> so
                    the product can be assigned to the specific subcategory
                    (or the parent itself, for general items). Categories
                    with no subcategories render as a plain option exactly
                    as before — zero visual change for tenants without a
                    category hierarchy. */}
                {categories.filter((c) => !c.parent_id).map((c) => {
                  const subs = categories.filter((s) => s.parent_id === c.id);
                  if (!subs.length) {
                    return <option key={c.id} value={c.id}>{c.name}</option>;
                  }
                  return (
                    <optgroup key={c.id} label={c.name}>
                      <option value={c.id}>{c.name} (general)</option>
                      {subs.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </optgroup>
                  );
                })}
              </select>
              <Input placeholder="Pack size" value={form.pack_size} onChange={(e) => setForm({ ...form, pack_size: e.target.value })} className="h-12 rounded-2xl" data-testid="prod-pack" />
            </div>
            <select value={form.brand_id} onChange={(e) => setForm({ ...form, brand_id: e.target.value })}
                    className="h-12 rounded-2xl border border-neutral-200 px-4 bg-white w-full" data-testid="prod-brand">
              <option value="">No brand (optional)</option>
              {brands.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <Input type="number" placeholder="Price *" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} className="h-12 rounded-2xl" data-testid="prod-price" />
              <Input type="number" placeholder="MRP" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: parseFloat(e.target.value) || 0 })} className="h-12 rounded-2xl" data-testid="prod-mrp" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-neutral-500 flex items-center gap-2">
                <ImageIcon className="w-3.5 h-3.5"/> Product images <span className="text-neutral-400 font-normal">({(form.images || []).length}/5)</span>
              </label>
              <div className="mt-2 flex flex-wrap gap-2 items-start" data-testid="prod-images-list">
                {(form.images || []).map((url, idx) => (
                  <div key={idx} className="relative group w-16 h-16 rounded-2xl overflow-hidden border border-neutral-200 bg-brand-cream"
                       data-testid={`prod-image-${idx}`}>
                    <img src={url} alt="" className="w-full h-full object-cover"/>
                    {idx === 0 && (
                      <span className="absolute top-0.5 left-0.5 text-[8px] font-extrabold uppercase bg-brand-sky text-white px-1 rounded" data-testid={`prod-image-primary-badge-${idx}`}>1st</span>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-0.5 text-white text-[10px]">
                      <div className="flex gap-1">
                        <button type="button" onClick={() => moveImage(idx, -1)} disabled={idx === 0} className="disabled:opacity-30 px-1.5 py-0.5 bg-white/20 rounded" data-testid={`prod-image-up-${idx}`}>↑</button>
                        <button type="button" onClick={() => moveImage(idx, 1)} disabled={idx === (form.images || []).length - 1} className="disabled:opacity-30 px-1.5 py-0.5 bg-white/20 rounded" data-testid={`prod-image-down-${idx}`}>↓</button>
                      </div>
                      <button type="button" onClick={() => removeImage(idx)} className="text-brand-clay font-bold hover:underline" data-testid={`prod-image-remove-${idx}`}>Remove</button>
                    </div>
                  </div>
                ))}
                {(form.images || []).length < 5 && (
                  <>
                    <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={(e) => pickImage(e.target.files?.[0])} data-testid="prod-image-file"/>
                    <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
                            className="w-16 h-16 rounded-2xl border-2 border-dashed border-brand-ink flex items-center justify-center bg-white hover:bg-brand-cream disabled:opacity-50"
                            data-testid="prod-upload-btn">
                      {uploading ? <span className="text-[10px]">…</span> : <Plus className="w-5 h-5 text-brand-ink"/>}
                    </button>
                  </>
                )}
              </div>
              <div className="text-[10px] text-neutral-500 mt-1">First image is shown in listings. Others appear in the product detail gallery.</div>
            </div>

            {/* ----- Variants editor ----- */}
            <div className="rounded-2xl border border-neutral-200 p-3 bg-brand-cream/30" data-testid="prod-variants-editor">
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs uppercase tracking-wider font-bold text-neutral-500">
                  Variants <span className="text-neutral-400 font-normal">({(form.variants || []).length})</span>
                </label>
                <Button variant="outline" onClick={addVariant}
                        className="h-8 rounded-full text-xs border-2 border-brand-ink font-bold"
                        data-testid="prod-add-variant-btn">
                  <Plus className="w-3 h-3 mr-1"/> Add variant
                </Button>
              </div>
              {(form.variants || []).length === 0 ? (
                <div className="text-[11px] text-neutral-500 leading-snug">
                  No custom variants — the product uses its top-level price / MRP / pack size as the single default variant. Add variants only if this product comes in multiple sizes / flavours.
                </div>
              ) : (
                <div className="space-y-2">
                  {(form.variants || []).map((v, idx) => (
                    <div key={v.id || idx} className="bg-white rounded-xl border border-neutral-200 p-2.5" data-testid={`prod-variant-row-${idx}`}>
                      <div className="flex items-center justify-between mb-1.5">
                        <div className="text-[10px] font-extrabold uppercase tracking-wider text-neutral-500">
                          Variant #{idx + 1} {idx === 0 && <span className="text-brand-sky">· primary</span>}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <label className="text-[10px] font-bold flex items-center gap-1 cursor-pointer" data-testid={`prod-variant-instock-label-${idx}`}>
                            <input type="checkbox" checked={v.is_in_stock !== false}
                                   onChange={(e) => updateVariant(idx, { is_in_stock: e.target.checked })}
                                   className="w-3.5 h-3.5" data-testid={`prod-variant-instock-${idx}`}/>
                            In stock
                          </label>
                          <button type="button" onClick={() => removeVariant(idx)}
                                  className="text-brand-clay hover:underline text-[11px] font-bold"
                                  data-testid={`prod-variant-remove-${idx}`}>
                            Remove
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input placeholder="Label (e.g. 500 ml)" value={v.label}
                               onChange={(e) => updateVariant(idx, { label: e.target.value })}
                               className="h-9 rounded-xl text-sm" data-testid={`prod-variant-label-${idx}`}/>
                        <Input placeholder="Pack size" value={v.pack_size}
                               onChange={(e) => updateVariant(idx, { pack_size: e.target.value })}
                               className="h-9 rounded-xl text-sm" data-testid={`prod-variant-pack-${idx}`}/>
                        <Input type="number" placeholder="Price" value={v.price}
                               onChange={(e) => updateVariant(idx, { price: e.target.value })}
                               className="h-9 rounded-xl text-sm" data-testid={`prod-variant-price-${idx}`}/>
                        <Input type="number" placeholder="MRP" value={v.mrp}
                               onChange={(e) => updateVariant(idx, { mrp: e.target.value })}
                               className="h-9 rounded-xl text-sm" data-testid={`prod-variant-mrp-${idx}`}/>
                        <Input placeholder="SKU (optional)" value={v.sku || ""}
                               onChange={(e) => updateVariant(idx, { sku: e.target.value })}
                               className="h-9 rounded-xl text-sm" data-testid={`prod-variant-sku-${idx}`}/>
                        <Input type="number" placeholder="Stock (info)" value={v.stock || 0}
                               onChange={(e) => updateVariant(idx, { stock: parseInt(e.target.value || 0, 10) })}
                               className="h-9 rounded-xl text-sm" data-testid={`prod-variant-stock-${idx}`}/>
                      </div>
                    </div>
                  ))}
                  <div className="text-[10px] text-neutral-500">
                    First variant&apos;s price / pack size stays synced to the product&apos;s top-level fields for existing subscriptions & invoices — do not reorder existing variants without care.
                  </div>
                </div>
              )}
            </div>

            <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="h-12 rounded-2xl" data-testid="prod-desc" />

            {/* Bottle deposit flag — controls whether delivering this SKU
                increments the customer's "bottles with you" counter and
                whether the rider sees a bottle-return prompt. */}
            <label
              className="flex items-start gap-3 rounded-2xl border-2 border-neutral-200 hover:border-brand-sky p-4 cursor-pointer transition-colors"
              data-testid="prod-has-bottle-row"
            >
              <input
                type="checkbox"
                checked={!!form.has_bottle}
                onChange={(e) => setForm({ ...form, has_bottle: e.target.checked })}
                className="mt-1 w-5 h-5 rounded accent-brand-sky cursor-pointer"
                data-testid="prod-has-bottle-checkbox"
              />
              <div className="flex-1">
                <div className="font-bold text-sm text-brand-ink">
                  Returnable bottle deposit
                </div>
                <div className="text-xs text-neutral-500 mt-0.5">
                  Enable only for SKUs sold in glass bottles (milk, buttermilk).
                  Curd, ghee, paneer etc. should stay OFF so the customer&apos;s
                  &quot;Bottles with you&quot; counter is not affected.
                </div>
              </div>
            </label>

            <Button onClick={save} className="h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold w-full" data-testid="save-product-btn">{editing ? "Save changes" : "Save Product"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <ImageCropDialog
        open={!!cropSrc}
        imageSrc={cropSrc}
        aspect={1}
        title="Crop product photo"
        onCancel={cancelCrop}
        onCropped={handleCropped}
      />
    </div>
  );
}

// ============ SUBSCRIPTIONS ============
export function AdminSubscriptions() {
  const [subs, setSubs] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [q, setQ] = useState("");
  const [routeFilter, setRouteFilter] = useState("__all__");
  const [cityFilter, setCityFilter] = useState("__all__");
  const [slotFilter, setSlotFilter] = useState("__all__");
  const [freqFilter, setFreqFilter] = useState("__all__");
  const [statusFilter, setStatusFilter] = useState("__all__");
  const [sortBy, setSortBy] = useState({ key: "created_at", dir: "desc" });
  const [editSub, setEditSub] = useState(null);
  const [editForm, setEditForm] = useState({ quantity: 1, slot: "morning", frequency: "daily", custom_days: [], instructions: "" });
  // "Change product / size" in the edit dialog — only in stores that have it
  // switched on (components/SubscriptionProductChange). The product list is
  // fetched the first time the dialog opens, and only then.
  const { tenant } = useAuth();
  const productChangeOn = isProductChangeOn(tenant);
  const [catalogue, setCatalogue] = useState(null);
  // The edit form as it was when the dialog opened — a product change closes
  // the dialog, so it is not offered while the form has edits of its own.
  const [editOpened, setEditOpened] = useState(null);
  const [pauseSub, setPauseSub] = useState(null);
  const [pauseFrom, setPauseFrom] = useState("");
  const [pauseUntil, setPauseUntil] = useState("");

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const load = async () => {
    setLoadError(null);
    try {
      const [{ data: s }, { data: r }] = await Promise.all([
        api.get("/api/admin/subscriptions"),
        api.get("/api/admin/routes"),
      ]);
      setSubs(s.subscriptions || []);
      setRoutes(r.routes || []);
    } catch (e) {
      setLoadError(friendlyMessage(e));
      toast.error(friendlyMessage(e));
    }
  };
  useEffect(() => { load().finally(() => setLoading(false)); /* eslint-disable-next-line */ }, []);

  const cities = Array.from(new Set(subs.map((s) => s.customer_city).filter(Boolean))).sort();

  const toggleSort = (key) => setSortBy((p) => p.key === key ? { key, dir: p.dir === "asc" ? "desc" : "asc" } : { key, dir: "asc" });
  const SortHeader = ({ k, children, align = "left" }) => (
    <th className={`text-${align} px-5 py-3 select-none cursor-pointer`} onClick={() => toggleSort(k)} data-testid={`sub-sort-${k}`}>
      <span className="inline-flex items-center gap-1">{children}{sortBy.key === k ? (sortBy.dir === "asc" ? <ChevronUp className="w-3 h-3"/> : <ChevronDown className="w-3 h-3"/>) : null}</span>
    </th>
  );

  const filtered = subs.filter((s) => {
    if (q.trim()) {
      const needle = q.toLowerCase();
      if (!(s.customer_name || "").toLowerCase().includes(needle) &&
          !(s.product_name || "").toLowerCase().includes(needle) &&
          !(s.customer_phone || "").includes(q.trim())) return false;
    }
    if (routeFilter === "__unassigned__" && s.customer_route_id) return false;
    if (routeFilter !== "__all__" && routeFilter !== "__unassigned__" && s.customer_route_id !== routeFilter) return false;
    if (cityFilter !== "__all__" && (s.customer_city || "") !== cityFilter) return false;
    if (slotFilter !== "__all__" && s.slot !== slotFilter) return false;
    if (freqFilter !== "__all__" && s.frequency !== freqFilter) return false;
    if (statusFilter !== "__all__" && s.status !== statusFilter) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const key = sortBy.key;
    const get = (x) => {
      switch (key) {
        case "customer_name": return (x.customer_name || "").toLowerCase();
        case "product_name": return (x.product_name || "").toLowerCase();
        case "route": return (x.customer_route_name || "~zzz").toLowerCase();
        case "city": return (x.customer_city || "~zzz").toLowerCase();
        case "slot": return x.slot || "";
        case "frequency": return x.frequency || "";
        case "quantity": return x.quantity || 0;
        case "per_delivery": return (x.price_per_unit || 0) * (x.quantity || 0);
        case "wallet": return x.wallet_balance || 0;
        case "status": return x.status || "";
        case "created_at": return x.created_at || "";
        default: return "";
      }
    };
    const av = get(a), bv = get(b);
    if (typeof av === "number" && typeof bv === "number") return sortBy.dir === "asc" ? av - bv : bv - av;
    if (av < bv) return sortBy.dir === "asc" ? -1 : 1;
    if (av > bv) return sortBy.dir === "asc" ? 1 : -1;
    return 0;
  });

  const openEdit = (s) => {
    setEditSub(s);
    const form = {
      quantity: s.quantity || 1,
      slot: s.slot || "morning",
      frequency: s.frequency || "daily",
      custom_days: s.custom_days || [],
      instructions: s.instructions || "",
    };
    setEditForm(form);
    setEditOpened(form);
    if (productChangeOn && !catalogue) {
      api.get("/api/admin/products").then(({ data }) => setCatalogue(data.products || [])).catch(() => setCatalogue([]));
    }
  };
  const saveEdit = async () => {
    try {
      await api.patch(`/api/admin/subscriptions/${editSub.id}`, editForm);
      toast.success("Subscription updated");
      setEditSub(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const resume = async (s) => {
    try { await api.patch(`/api/admin/subscriptions/${s.id}/resume`); toast.success("Resumed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const cancelSub = async (s) => {
    if (!window.confirm(`Cancel ${s.customer_name}'s subscription for ${s.product_name}? This cannot be undone.`)) return;
    try { await api.delete(`/api/admin/subscriptions/${s.id}`); toast.success("Cancelled"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const submitPause = async () => {
    if (!pauseFrom || !pauseUntil) { toast.error("Pick from + until"); return; }
    try {
      await api.patch(`/api/admin/subscriptions/${pauseSub.id}/pause`, { pause_from: pauseFrom, pause_until: pauseUntil });
      toast.success("Paused");
      setPauseSub(null); setPauseFrom(""); setPauseUntil("");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const DAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  if (loading) return <LoadingState fullHeight />;
  if (loadError) {
    return (
      <EmptyState icon={AlertTriangle} title="Couldn't load subscriptions" description={loadError}
        action={<button onClick={() => { setLoading(true); load().finally(() => setLoading(false)); }} className="h-10 px-4 rounded-2xl bg-brand-sky text-white text-sm font-bold">Retry</button>}
        fullHeight />
    );
  }

  return (
    <div className="space-y-6 page-enter" data-testid="admin-subscriptions-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider font-bold text-neutral-500">Recurring Revenue</div>
          <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Subscriptions ({subs.length})</h1>
        </div>
        <div className="relative w-full sm:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"/>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search customer, product, phone…"
                 className="pl-10 h-11 rounded-2xl" data-testid="subs-search-input"/>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mr-1">Filters</span>
        <select value={routeFilter} onChange={(e) => setRouteFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white" data-testid="subs-route-filter">
          <option value="__all__">All routes</option>
          <option value="__unassigned__">Unassigned</option>
          {filterableRoutes(routes).map((r) => <option key={r.id} value={r.id}>{routeOptionLabel(r)}</option>)}
        </select>
        <select value={cityFilter} onChange={(e) => setCityFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white" data-testid="subs-city-filter">
          <option value="__all__">All cities</option>
          {cities.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={slotFilter} onChange={(e) => setSlotFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white" data-testid="subs-slot-filter">
          <option value="__all__">All slots</option>
          <option value="morning">Morning</option>
          <option value="evening">Evening</option>
          <option value="both">Both</option>
        </select>
        <select value={freqFilter} onChange={(e) => setFreqFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white" data-testid="subs-freq-filter">
          <option value="__all__">All frequencies</option>
          <option value="daily">Daily</option>
          <option value="alternate">Alternate</option>
          <option value="weekly">Weekly</option>
          <option value="custom">Custom</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                className="h-10 px-3 rounded-full text-sm font-bold border border-neutral-200 bg-white" data-testid="subs-status-filter">
          <option value="__all__">All statuses</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="cancelled">Cancelled</option>
          <option value="expired">Expired</option>
        </select>
        <span className="text-xs text-neutral-500 ml-auto" data-testid="subs-count">{sorted.length} of {subs.length}</span>
      </div>

      <div className="bg-white rounded-3xl border border-neutral-200 overflow-hidden">
        {/* Mobile card list */}
        <ul className="md:hidden divide-y divide-neutral-100" data-testid="sub-mobile-list">
          {sorted.length === 0 && <li><EmptyState icon={Search} size="sm" title="No subscriptions match" /></li>}
          {sorted.map((s) => (
            <li key={s.id} className="p-4" data-testid={`sub-mobile-${s.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-bold text-base truncate">{s.customer_name}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${
                      s.status === "active" ? "bg-brand-leaf/10 text-brand-leaf" :
                      s.status === "paused" ? "bg-brand-sand/15 text-brand-sand" :
                      "bg-neutral-100 text-neutral-500"
                    }`} data-testid={`sub-status-${s.id}`}>{s.status}</span>
                  </div>
                  <div className="text-xs text-neutral-500 font-mono mt-0.5">{s.customer_phone}</div>
                  <CustomerUnit address={s.customer_unit} requireFlat={tenant?.require_flat_number === true} className="mt-0.5" testId={`sub-unit-${s.id}`}/>
                  <div className="text-sm text-neutral-700 mt-1">
                    {s.product_name} <span className="text-neutral-400">·</span> <span className="capitalize">{s.slot}</span> <span className="text-neutral-400">·</span> <span className="font-mono">×{s.quantity}</span>
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-0.5">
                    <span className="capitalize font-semibold">{s.frequency}</span>
                    {s.schedule_label && s.frequency !== "daily" && <span> · {s.schedule_label}</span>}
                    {s.start_date && <span> · starts {s.start_date}</span>}
                  </div>
                  {s.status === "paused" && (
                    <div className="text-[11px] text-brand-sand font-bold mt-0.5" data-testid={`sub-pause-window-${s.id}`}>{pauseSummary(s, formatDate)}</div>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="font-mono font-bold">{formatINR((s.price_per_unit || 0) * (s.quantity || 0))}</div>
                  <div className="text-[10px] uppercase tracking-wider text-neutral-500">per drop</div>
                  <div className={`text-[11px] font-mono font-bold mt-1 ${s.wallet_balance < 100 ? "text-brand-clay" : "text-neutral-700"}`}>Wallet {formatINR(s.wallet_balance || 0)}</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1.5 mt-2.5">
                {s.status === "active" && (
                  <>
                    <button onClick={() => openEdit(s)} className="h-8 px-2.5 rounded-full bg-brand-sky/10 text-brand-sky text-[10px] font-bold inline-flex items-center gap-1" data-testid={`sub-edit-${s.id}`}>
                      <Edit className="w-3 h-3"/> Edit
                    </button>
                    <button onClick={() => { setPauseSub(s); setPauseFrom(""); setPauseUntil(""); }} className="h-8 px-2.5 rounded-full bg-brand-sand/15 text-brand-sand text-[10px] font-bold" data-testid={`sub-pause-${s.id}`}>Pause</button>
                    <button onClick={() => cancelSub(s)} className="h-8 px-2.5 rounded-full bg-brand-clay/10 text-brand-clay text-[10px] font-bold inline-flex items-center gap-1" data-testid={`sub-cancel-${s.id}`}>
                      <Trash2 className="w-3 h-3"/> Cancel
                    </button>
                  </>
                )}
                {s.status === "paused" && (
                  <>
                    <button onClick={() => resume(s)} className="h-8 px-2.5 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold" data-testid={`sub-resume-${s.id}`}>Resume</button>
                    <button onClick={() => cancelSub(s)} className="h-8 px-2.5 rounded-full bg-brand-clay/10 text-brand-clay text-[10px] font-bold inline-flex items-center gap-1" data-testid={`sub-cancel-${s.id}`}>
                      <Trash2 className="w-3 h-3"/> Cancel
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>

        <div className="hidden md:block overflow-x-auto">
        <table className="w-full min-w-[1100px]">
          <thead className="bg-neutral-50 text-xs uppercase tracking-wider font-bold text-neutral-500 border-b border-neutral-200">
            <tr>
              <SortHeader k="customer_name">Customer</SortHeader>
              <SortHeader k="route">Route</SortHeader>
              <SortHeader k="city">City</SortHeader>
              <SortHeader k="product_name">Product</SortHeader>
              <SortHeader k="frequency">Schedule</SortHeader>
              <SortHeader k="slot">Slot</SortHeader>
              <SortHeader k="quantity" align="right">Qty</SortHeader>
              <SortHeader k="per_delivery" align="right">Per delivery</SortHeader>
              <SortHeader k="wallet" align="right">Wallet</SortHeader>
              <SortHeader k="status">Status</SortHeader>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {sorted.map((s) => (
              <tr key={s.id} data-testid={`sub-row-${s.id}`} className="hover:bg-neutral-50">
                <td className="px-5 py-3">
                  <div className="font-semibold">{s.customer_name}</div>
                  <div className="text-xs text-neutral-500 font-mono">{s.customer_phone}</div>
                  <CustomerUnit address={s.customer_unit} requireFlat={tenant?.require_flat_number === true} className="max-w-[220px]" testId={`sub-unit-row-${s.id}`}/>
                </td>
                <td className="px-5 py-3 text-sm">
                  {s.customer_route_name ? (
                    <span className="px-2 py-0.5 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold uppercase tracking-wider">{s.customer_route_name}</span>
                  ) : <span className="text-neutral-400 text-xs">—</span>}
                </td>
                <td className="px-5 py-3 text-sm">{s.customer_city || "—"}</td>
                <td className="px-5 py-3">{s.product_name}</td>
                <td className="px-5 py-3 text-sm" data-testid={`sub-schedule-${s.id}`}>
                  <div className="font-semibold capitalize">{s.frequency}</div>
                  {s.schedule_label && s.frequency !== "daily" && (
                    <div className="text-xs text-neutral-500 mt-0.5">{s.schedule_label}</div>
                  )}
                  {s.start_date && (
                    <div className="text-[10px] text-neutral-400 mt-0.5">starts {s.start_date}</div>
                  )}
                </td>
                <td className="px-5 py-3 capitalize text-sm">{s.slot}</td>
                <td className="px-5 py-3 text-right font-mono">{s.quantity}</td>
                <td className="px-5 py-3 text-right font-mono font-bold">{formatINR((s.price_per_unit || 0) * (s.quantity || 0))}</td>
                <td className="px-5 py-3 text-right font-mono font-bold text-sm">
                  <span className={s.wallet_balance < 100 ? "text-brand-clay" : "text-neutral-700"} data-testid={`sub-wallet-${s.id}`}>{formatINR(s.wallet_balance || 0)}</span>
                </td>
                <td className="px-5 py-3">
                  <span className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase ${
                    s.status === "active" ? "bg-brand-leaf/10 text-brand-leaf" :
                    s.status === "paused" ? "bg-brand-sand/15 text-brand-sand" :
                    "bg-neutral-100 text-neutral-500"
                  }`} data-testid={`sub-status-${s.id}`}>{s.status}</span>
                  {s.status === "paused" && (
                    <div className="text-[11px] text-brand-sand font-bold mt-1 whitespace-nowrap" data-testid={`sub-pause-window-row-${s.id}`}>{pauseSummary(s, formatDate)}</div>
                  )}
                </td>
                <td className="px-5 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {s.status === "active" && (
                      <>
                        <button onClick={() => openEdit(s)} className="h-8 px-2.5 rounded-full bg-brand-sky/10 text-brand-sky text-[10px] font-bold inline-flex items-center gap-1" data-testid={`sub-edit-${s.id}`}>
                          <Edit className="w-3 h-3"/> Edit
                        </button>
                        <button onClick={() => { setPauseSub(s); setPauseFrom(""); setPauseUntil(""); }} className="h-8 px-2.5 rounded-full bg-brand-sand/15 text-brand-sand text-[10px] font-bold" data-testid={`sub-pause-${s.id}`}>Pause</button>
                        <button onClick={() => cancelSub(s)} className="h-8 px-2.5 rounded-full bg-brand-clay/10 text-brand-clay text-[10px] font-bold inline-flex items-center gap-1" data-testid={`sub-cancel-${s.id}`}>
                          <Trash2 className="w-3 h-3"/> Cancel
                        </button>
                      </>
                    )}
                    {s.status === "paused" && (
                      <>
                        <button onClick={() => resume(s)} className="h-8 px-2.5 rounded-full bg-brand-leaf/10 text-brand-leaf text-[10px] font-bold" data-testid={`sub-resume-${s.id}`}>Resume</button>
                        <button onClick={() => cancelSub(s)} className="h-8 px-2.5 rounded-full bg-brand-clay/10 text-brand-clay text-[10px] font-bold inline-flex items-center gap-1" data-testid={`sub-cancel-${s.id}`}>
                          <Trash2 className="w-3 h-3"/> Cancel
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {sorted.length === 0 && <tr><td colSpan="11"><EmptyState icon={Search} size="sm" title="No subscriptions match the filters" testId="subs-empty" /></td></tr>}
          </tbody>
        </table>
        </div>
      </div>

      {/* Edit subscription dialog (cutoff bypassed for admin) */}
      <Dialog open={!!editSub} onOpenChange={(o) => !o && setEditSub(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="sub-edit-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Edit subscription</DialogTitle></DialogHeader>
          {editSub && (
            <div className="space-y-3">
              <div className="bg-brand-cream rounded-2xl p-3 text-sm">
                <div className="font-bold">{editSub.customer_name}</div>
                <div className="text-xs text-neutral-600">{editSub.product_name}</div>
              </div>
              {/* Only while something is actually left to do: a finished change keeps its
                  marker for a couple of minutes with nothing wrong (last_error empty). */}
              {editSub.product_sync_pending?.last_error && (
                <div className="rounded-2xl bg-amber-50 border border-amber-300 px-3 py-2 text-xs text-amber-900" data-testid="sub-edit-product-pending">
                  A product change on this subscription is still being applied to its upcoming deliveries. {editSub.product_sync_pending.last_error}
                </div>
              )}
              {productChangeOn && (
                <SubscriptionProductChange
                  key={editSub.id}
                  sub={editSub} products={catalogue || []} admin
                  endpoint={`/api/admin/subscriptions/${editSub.id}/product-change`}
                  unsaved={hasUnsavedEdits(editOpened, editForm)}
                  onChanged={(data, outcome, { late } = {}) => {
                    load();
                    // An answer that arrives after its dialog was closed touches no dialog —
                    // by then this one may be showing another subscription.
                    if (late) return;
                    // Done: close, so nobody is left to "save" a form they never changed.
                    // Still being applied: stay open on the status the step shows.
                    setEditSub((s) => dialogAfterChange(s, editSub.id, data, outcome));
                  }}
                  onClose={() => setEditSub((s) => (s && s.id === editSub.id ? null : s))}
                />
              )}
              <Field label="Quantity">
                <Input type="number" min={1} max={50} value={editForm.quantity}
                       onChange={(e) => setEditForm({ ...editForm, quantity: parseInt(e.target.value) || 1 })}
                       className="h-11 rounded-2xl" data-testid="sub-edit-quantity"/>
              </Field>
              <Field label="Slot">
                <select value={editForm.slot} onChange={(e) => setEditForm({ ...editForm, slot: e.target.value })}
                        className="h-11 w-full rounded-2xl border border-neutral-200 px-3 bg-white" data-testid="sub-edit-slot">
                  <option value="morning">Morning</option>
                  <option value="evening">Evening</option>
                  <option value="both">Both</option>
                </select>
              </Field>
              <Field label="Frequency">
                <select value={editForm.frequency} onChange={(e) => setEditForm({ ...editForm, frequency: e.target.value })}
                        className="h-11 w-full rounded-2xl border border-neutral-200 px-3 bg-white" data-testid="sub-edit-frequency">
                  <option value="daily">Daily</option>
                  <option value="alternate">Alternate</option>
                  <option value="weekly">Weekly</option>
                  <option value="custom">Custom</option>
                </select>
              </Field>
              {(editForm.frequency === "weekly" || editForm.frequency === "custom") && (
                <Field label="Days">
                  <div className="flex gap-1 flex-wrap">
                    {DAY_LABELS.map((d, i) => {
                      const on = editForm.custom_days.includes(i);
                      return (
                        <button key={i}
                                onClick={() => setEditForm((p) => ({ ...p, custom_days: on ? p.custom_days.filter((x) => x !== i) : [...p.custom_days, i] }))}
                                className={`h-9 px-3 rounded-full text-xs font-bold ${on ? "bg-brand-sky text-white" : "bg-white border border-neutral-200 text-neutral-700"}`}
                                data-testid={`sub-edit-day-${i}`}>{d}</button>
                      );
                    })}
                  </div>
                </Field>
              )}
              <Field label="Instructions">
                <Input value={editForm.instructions || ""} onChange={(e) => setEditForm({ ...editForm, instructions: e.target.value })}
                       placeholder="Leave at door…" className="h-11 rounded-2xl" data-testid="sub-edit-instructions"/>
              </Field>
              <div className="rounded-2xl bg-brand-sand/10 border border-brand-sand/30 px-3 py-2 text-[11px] text-brand-ink/80">
                Tenant edits bypass the cutoff window.
              </div>
              <Button onClick={saveEdit} className="w-full h-12 rounded-2xl bg-brand-sky hover:bg-blue-700 font-bold" data-testid="sub-edit-save">Save changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Pause dialog */}
      <Dialog open={!!pauseSub} onOpenChange={(o) => !o && setPauseSub(null)}>
        <DialogContent className="max-w-md rounded-3xl" data-testid="sub-pause-dialog">
          <DialogHeader><DialogTitle className="font-heading text-2xl">Pause subscription</DialogTitle></DialogHeader>
          {pauseSub && (
            <div className="space-y-3">
              <div className="bg-brand-cream rounded-2xl p-3 text-sm">
                <div className="font-bold">{pauseSub.customer_name}</div>
                <div className="text-xs text-neutral-600">{pauseSub.product_name}</div>
              </div>
              <Field label="Pause from">
                <Input type="date" value={pauseFrom} onChange={(e) => setPauseFrom(e.target.value)} className="h-11 rounded-2xl" data-testid="sub-pause-from"/>
              </Field>
              <Field label="Resume on">
                <Input type="date" value={pauseUntil} min={pauseFrom || undefined} onChange={(e) => setPauseUntil(e.target.value)} className="h-11 rounded-2xl" data-testid="sub-pause-until"/>
              </Field>
              <Button onClick={submitPause} className="w-full h-12 rounded-2xl bg-brand-sand hover:opacity-90 font-bold text-white" data-testid="sub-pause-confirm">Confirm pause</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}


function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1">{label}</div>
      {children}
    </label>
  );
}
